// Server helpers must not initialize browser analytics through the package barrel.
// eslint-disable-next-line no-restricted-imports
import { logger } from '@twisto/utils/logger'

import type { CmsClientError } from './fetch-json'
import {
  CmsUnavailableError,
  fetchJson,
  isCmsClientError,
  isCmsNotFoundError,
} from './fetch-json'

const FRESH_MS = 60_000
const STALE_MS = 3_600_000
const MISSING_MS = 10_000
const RETRY_MS = 5000
const MAX_ENTRIES = 1024
const MAX_METADATA_ENTRIES = 256
const MAX_BYTES = 16_777_216
const MAX_PENDING = 32
const BYTES_PER_CHARACTER = 2

type Entry = {
  body?: string
  clientError?: CmsClientError
  freshUntil: number
  staleUntil: number
  retryAfter: number
}

type Cache = {
  entries: Map<string, Entry>
  metadata: Map<string, Entry>
  pending: Map<string, Promise<string>>
  bytes: number
  generation: number
  purgeGeneration: number
}

const globals = globalThis as typeof globalThis & {
  homepageCmsCache?: Cache
}

// Share across Next's route bundles, but never cache CMS data in the browser.
const cache = (globals.homepageCmsCache ??= {
  entries: new Map(),
  metadata: new Map(),
  pending: new Map(),
  bytes: 0,
  generation: 0,
  purgeGeneration: 0,
})

const remove = (key: string) => {
  cache.bytes -=
    (cache.entries.get(key)?.body?.length ?? 0) * BYTES_PER_CHARACTER
  cache.entries.delete(key)
}

const store = (key: string, entry: Entry) => {
  remove(key)
  const bytes = (entry.body?.length ?? 0) * BYTES_PER_CHARACTER
  if (bytes > MAX_BYTES) {
    return
  }
  const now = Date.now()
  cache.entries.forEach((value, entryKey) => {
    if (now >= value.staleUntil) {
      remove(entryKey)
    }
  })
  while (cache.entries.size >= MAX_ENTRIES || cache.bytes + bytes > MAX_BYTES) {
    remove(cache.entries.keys().next().value)
  }
  cache.entries.set(key, entry)
  cache.bytes += bytes
}

const storeMetadata = (key: string, entry: Entry) => {
  const now = Date.now()
  cache.metadata.forEach((value, entryKey) => {
    if (now >= Math.max(value.staleUntil, value.retryAfter)) {
      cache.metadata.delete(entryKey)
    }
  })
  cache.metadata.delete(key)
  if (cache.metadata.size >= MAX_METADATA_ENTRIES) {
    cache.metadata.delete(cache.metadata.keys().next().value)
  }
  cache.metadata.set(key, entry)
}

export const isCmsPreview = (url: URL) =>
  /\/draft\/?$/.test(decodeURIComponent(url.pathname)) ||
  url.searchParams.get('draft') === 'true' ||
  url.searchParams.get('status') === 'draft' ||
  url.searchParams.get('publicationState') === 'preview'

const cacheKey = (url: URL) => {
  url.searchParams.sort()

  return url.toString()
}

export const getCachedCmsJson = <T>(requestUrl: string): T | null => {
  const url = new URL(requestUrl)
  if (typeof window !== 'undefined' || isCmsPreview(url)) {
    return null
  }
  const key = cacheKey(url)
  const entry = cache.entries.get(key)
  if (!entry?.body || Date.now() >= entry.staleUntil) {
    return null
  }
  cache.entries.delete(key)
  cache.entries.set(key, entry)

  return JSON.parse(entry.body)
}

const load = async (key: string): Promise<string> => {
  const data = await fetchJson<unknown>(key)
  const path = new URL(key).pathname
  if (
    !data ||
    typeof data !== 'object' ||
    Array.isArray(data) ||
    (!('data' in data) &&
      !path.endsWith('/conditions-page/root') &&
      !path.endsWith('/fuzzy-search/search'))
  ) {
    throw new CmsUnavailableError()
  }

  return JSON.stringify(data)
}

const refresh = (key: string): Promise<string> => {
  const pending = cache.pending.get(key)
  if (pending) {
    return pending
  }
  if (cache.pending.size >= MAX_PENDING) {
    return Promise.reject(new CmsUnavailableError())
  }
  const generation = cache.generation
  const purgeGeneration = cache.purgeGeneration
  const promise = load(key)
    .then((body) => {
      if (purgeGeneration !== cache.purgeGeneration) {
        throw new CmsUnavailableError()
      }
      // Soft invalidation must not turn a successful response into a visitor error.
      if (generation === cache.generation) {
        const data = JSON.parse(body).data
        const missing =
          data === null || (Array.isArray(data) && data.length === 0)
        const now = Date.now()
        cache.metadata.delete(key)
        if (missing) {
          remove(key)
          storeMetadata(key, {
            body,
            freshUntil: now + MISSING_MS,
            staleUntil: now + MISSING_MS,
            retryAfter: 0,
          })
        } else {
          store(key, {
            body,
            freshUntil: now + FRESH_MS,
            staleUntil: now + STALE_MS,
            retryAfter: 0,
          })
        }
      }

      return body
    })
    .catch((error) => {
      if (generation === cache.generation) {
        if (isCmsClientError(error)) {
          remove(key)
          const until =
            Date.now() + (isCmsNotFoundError(error) ? MISSING_MS : RETRY_MS)
          storeMetadata(key, {
            clientError: error,
            freshUntil: until,
            staleUntil: until,
            retryAfter: 0,
          })
          throw error
        }
        const entry = cache.entries.get(key)
        if (entry) {
          cache.entries.set(key, {
            ...entry,
            freshUntil: 0,
            retryAfter: Date.now() + RETRY_MS,
          })
        } else {
          storeMetadata(key, {
            freshUntil: 0,
            staleUntil: 0,
            retryAfter: Date.now() + RETRY_MS,
          })
        }
        logger.logError('CMS refresh failed', {
          path: new URL(key).pathname,
        })
      }
      throw error
    })
    .finally(() => cache.pending.delete(key))
  cache.pending.set(key, promise)

  return promise
}

export const invalidateCmsCache = (purge = false) => {
  cache.generation += 1
  cache.metadata.clear()
  if (purge) {
    cache.purgeGeneration += 1
    cache.entries.clear()
    cache.bytes = 0
  } else {
    cache.entries.forEach((entry, key) => {
      cache.entries.set(key, { ...entry, freshUntil: 0, retryAfter: 0 })
    })
  }
}

export const fetchCmsJson = async <T>(
  requestUrl: string,
  useCache = true
): Promise<T> => {
  const url = new URL(requestUrl)
  const key = cacheKey(url)
  if (typeof window !== 'undefined' || !useCache || isCmsPreview(url)) {
    return JSON.parse(await load(key))
  }

  const entry = cache.entries.get(key) ?? cache.metadata.get(key)
  const now = Date.now()
  if (entry?.clientError && now < entry.staleUntil) {
    throw entry.clientError
  }
  if (entry?.body && now < entry.staleUntil) {
    // Refresh LRU order without changing freshness or extending stale lifetime.
    if (cache.entries.has(key)) {
      cache.entries.delete(key)
      cache.entries.set(key, entry)
    }
    if (now >= entry.freshUntil && now >= entry.retryAfter) {
      refresh(key).catch(() => undefined)
    }

    return JSON.parse(entry.body)
  }
  if (entry && now < entry.retryAfter) {
    throw new CmsUnavailableError()
  }

  // shortcut: each instance warms independently; use shared storage if restart continuity is required.
  return JSON.parse(await refresh(key))
}
